import { COUNTRIES_BY_CODE } from '~/app/shared/util/utilities/countries';
import * as Yup from 'yup';
import { t } from '@lingui/macro';
import { BankAccountStepState, CompanyInfoStepState, CreateCompanyRequestBody, CustomerType, SubscriptionType } from '../types';

/**
 * For context, in e-conomic, the phone number is stored in a single field,
 * whereas in payroll, we have two fields: one for the calling code and one for the phone number.
 * Given that the company data in this onboarding step is pre-filled with e-conomic data, we this custom
 * logic to address this difference.
 * It goes as follows:
 *  - Both the calling code and the phone number inputs will always be present (even if they are empty)
 *  - If the phone number has 8 digits, we assume it is a Danish phone number. So the 8 digits go to the
 * number input and we pre-fill the calling code dropdown with the Danish calling code (+45)
 *  - If the phone number already includes a +45, we split that part and fill both inputs accordingly
 *  - If the phone number is anything else, we pre-fill the calling code dropdown with +45 and leave the number input empty
 */
export const treatPhoneNumber = (untreatedPhoneNumber: string) => {
    // Default to Danish calling code
    const defaultCallingCode = COUNTRIES_BY_CODE.DK.callingCode;
    const defaultCallingCodeValue = `${COUNTRIES_BY_CODE.DK.code}#${defaultCallingCode}`;

    // Return default values if phone number is empty
    if (!untreatedPhoneNumber) {
        return { callingCode: defaultCallingCodeValue, number: '' };
    }

    // Clean the phone number by removing spaces and dashes
    const phoneNumber = untreatedPhoneNumber.replace(/[\s-()]/g, '');

    // Check if it starts with Danish calling code
    if (phoneNumber.startsWith(defaultCallingCode)) {
        const phoneNumberWithoutCallingCode = phoneNumber.slice(defaultCallingCode.length);
        return {
            callingCode: defaultCallingCodeValue,
            number: phoneNumberWithoutCallingCode.length === 8 ? phoneNumberWithoutCallingCode : '',
        };
    }

    // Treat as Danish number if it has exactly 8 digits, else, empty number
    return {
        callingCode: defaultCallingCodeValue,
        number: !phoneNumber.includes('+') && phoneNumber.length === 8 ? phoneNumber : '',
    };
};

export const getCompanyInfoStepValidationSchema = () => {
    const validEmailMessage = t`Must be a valid email`;
    const validPhoneNumberMessage = t`Must be a valid 8-digit number`;

    const requiredStringField = Yup.string().required();
    const requiredEmailField = requiredStringField.email(validEmailMessage);
    const requiredPhoneNumberField = requiredStringField
        .matches(/^[0-9]+$/, validPhoneNumberMessage)
        .length(8, validPhoneNumberMessage);

    return Yup.object<CompanyInfoStepState>({
        cvrOrSENumber: requiredStringField,
        companyName: requiredStringField,
        emailForInvoicing: requiredEmailField,
        companyCallingCode: requiredStringField,
        companyPhoneNumber: requiredPhoneNumberField,
        postCode: requiredStringField,
        city: requiredStringField,
        address: requiredStringField,
        contactPersonName: requiredStringField,
        contactPersonCallingCode: requiredStringField,
        contactPersonPhoneNumber: requiredPhoneNumberField,
        contactPersonEmail: requiredEmailField,
    });
};

export const getBankAccountStepValidationSchema = ({ isCVR }: { isCVR: boolean }) => {
    const validEmailMessage = t`Must be a valid email`;
    const valid4DigitNumberMessage = t`Must be a valid 4-digit number`;
    const valid8DigitNumberMessage = t`Must be a valid 8-digit number`;

    const requiredStringField = Yup.string().required();
    const requiredEmailField = requiredStringField.email(validEmailMessage);
    const required4digitNumberField = requiredStringField
        .matches(/^[0-9]+$/, valid4DigitNumberMessage)
        .length(4, valid4DigitNumberMessage);

    const required8digitNumberField = requiredStringField
        .matches(/^[0-9]+$/, valid8DigitNumberMessage)
        .length(8, valid8DigitNumberMessage);

    const baseSchema = {
        regNo: required4digitNumberField,
        accountNo: requiredStringField
            .matches(/^[0-9]+$/, t`Account no. can only contain numbers.`)
            .min(8)
            .max(10),
    };

    if (isCVR) {
        return Yup.object({
            ...baseSchema,
            contactName: requiredStringField,
            callingCode: requiredStringField,
            phoneNumber: required8digitNumberField,
            email: requiredEmailField,
        });
    }

    return Yup.object(baseSchema);
};

/**
 * Helper function that given the state of the company info step and the bank account step,
 * returns the body to be used in the create company request.
 */
export const getCreateCompanyBody = (
    bankAccountStepState: BankAccountStepState,
    companyInfoStepState?: CompanyInfoStepState
): CreateCompanyRequestBody => {
    const companyToCreate: CreateCompanyRequestBody = {
        cvrNumber: companyInfoStepState?.cvrOrSENumber || '',
        name1: companyInfoStepState?.companyName || '',
        name2: '',
        address: companyInfoStepState?.address || '',
        zipCode: companyInfoStepState?.postCode || '',
        city: companyInfoStepState?.city || '',
        phoneNumber: companyInfoStepState?.companyPhoneNumber || '',
        contactPerson: {
            fullName: companyInfoStepState?.contactPersonName || '',
            phoneNumber: companyInfoStepState?.contactPersonPhoneNumber || '',
            mobilePhoneNumber: companyInfoStepState?.contactPersonPhoneNumber || '',
            email: companyInfoStepState?.contactPersonEmail || '',
        },
        invoiceEmail: companyInfoStepState?.emailForInvoicing || '',
        bankDetails: {
            registrationNumber: bankAccountStepState.regNo,
            accountNumber: bankAccountStepState.accountNo,
        },
        customerType: CustomerType.Company,
        administratorCompanyNumber: '',
        specialAgreement: '',
        vendorId: '',
        esvType: '',
        isSalaryAdministrationCustomer: false,
        subscriptionType: SubscriptionType.Differentiated,
    };

    return companyToCreate;
};
